import React, { useState, useEffect, useRef } from 'react';
import { Send, Image as ImageIcon, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMessageStore } from '../stores/messageStore';
import { useListingStore } from '../stores/listingStore';
import { sanitizeMessage } from '../utils/contactFilter';
import { useUserStore } from '../stores/userStore';
import { formatPrice } from '../utils/formatters';

interface ChatInterfaceProps {
  conversationId: string;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ conversationId }) => {
  const [message, setMessage] = useState('');
  const { user } = useAuth();
  const { conversations, addMessage, markConversationAsRead } = useMessageStore();
  const { users } = useUserStore();
  const { listings } = useListingStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasMarkedAsRead = useRef(false);

  const conversation = conversations.find(c => c.id === conversationId);
  const listing = listings.find(l => l.id === conversation?.listingId);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
    // Only mark as read once when conversation changes
    if (conversation && user && !hasMarkedAsRead.current) {
      markConversationAsRead(conversationId, user.id);
      hasMarkedAsRead.current = true;
    }
  }, [conversation?.messages]);

  // Reset hasMarkedAsRead when conversation changes
  useEffect(() => {
    hasMarkedAsRead.current = false;
  }, [conversationId]);

  const getRecipientName = (senderId: string) => {
    const recipientUser = users[senderId];
    return recipientUser?.fullName?.split(' ')[0] || 'Unknown User';
  };

  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !message.trim() || !conversation) return;

    // Check for contact information
    const { isValid, sanitizedText } = sanitizeMessage(message);
    if (!isValid) {
      alert("Direct contact information is not allowed. Please use the platform's messaging system.");
      setMessage(sanitizedText);
      return;
    }

    // Get the other participant's ID
    const receiverId = conversation.participants.find(id => id !== user.id);
    if (!receiverId) return;

    addMessage(conversationId, {
      senderId: user.id,
      content: message.trim(),
      isRead: false
    });
    setMessage('');
  };

  if (!conversation || !listing) return null;

  return (
    <div className="flex flex-col h-[600px] bg-gray-50">
      {/* Chat Header */}
      <div className="p-4 bg-white border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">{conversation.listingTitle}</h3>
            <p className="text-sm text-gray-600">
              Chatting with {getRecipientName(conversation.participants.find(id => id !== user?.id) || '')}
            </p>
          </div>
        </div>

        {/* Property Preview */}
        <Link 
          to={`/listings/${listing.id}`}
          className="mt-3 flex items-center p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <div className="h-16 w-16 rounded-lg overflow-hidden flex-shrink-0">
            <img 
              src={listing.images[0]} 
              alt={listing.title}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="ml-3 flex-1">
            <h4 className="font-medium text-gray-900">{listing.title}</h4>
            <p className="text-sm text-gray-500">{listing.location}</p>
            <p className="text-sm font-medium text-indigo-600">{formatPrice(listing.price)}/mo</p>
          </div>
          <Building2 className="h-5 w-5 text-gray-400 flex-shrink-0" />
        </Link>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-4">
          {conversation.messages.map((msg, index) => (
            <div
              key={msg.id}
              className={`flex ${msg.senderId === user?.id ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                msg.senderId === user?.id
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-900 shadow-sm'
              }`}>
                <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                <p className={`text-xs mt-1 ${
                  msg.senderId === user?.id ? 'text-indigo-100' : 'text-gray-500'
                }`}>
                  {new Date(msg.timestamp).toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="p-4 bg-white border-t">
        <form onSubmit={handleSend} className="flex space-x-2">
          <div className="flex-1 relative">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type a message..."
              className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none h-[42px] leading-6 overflow-auto"
              style={{ minHeight: '42px', maxHeight: '120px' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (message.trim()) {
                    handleSend(e);
                  }
                }
              }}
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
            >
              <ImageIcon className="h-5 w-5" />
            </button>
          </div>
          <button
            type="submit"
            disabled={!message.trim()}
            className="bg-indigo-600 text-white p-2 rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            <Send className="h-5 w-5" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChatInterface;